import { gql, useMutation } from '@apollo/client';
import { notification } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { GET_RECRUITMENT_BOARDERS } from 'src/gql/queries/recruitment';
import { AcceptRecruitmentRequestInput } from 'src/shared/types/api';
import { BoarderRecruitmentDoc } from 'src/shared/types/docs';
import { UseOnMutation } from '..';

export const ACCEPT_RECRUITMENT_REQUEST = gql`
  mutation AcceptRecruitmentRequest($input: AcceptRecruitmentRequestInput!) {
    acceptRecruitmentRequest(input: $input) {
      boarderRecruitmentId
    }
  }
`;

type useAcceptRecruitmentRequestArgs =
  Pick<BoarderRecruitmentDoc, 'boarderRecruitmentId'> & {
    onSuccess?: () => void;
    onComplete?: () => void;
  }

export const useAcceptRecruitmentRequest = (
  { boarderRecruitmentId, onSuccess, onComplete }: useAcceptRecruitmentRequestArgs,
): UseOnMutation => {
  const { t } = useTranslation();
  const [acceptRecruitment, { loading }] = useMutation<
    unknown, { input: AcceptRecruitmentRequestInput }
  >(ACCEPT_RECRUITMENT_REQUEST, { refetchQueries: [GET_RECRUITMENT_BOARDERS] });

  const onAcceptRecruitmentRequest = useCallback(async () => {
    try {
      await acceptRecruitment({
        variables: {
          input: {
            boarderRecruitmentId,
          },
        },
      });
      onSuccess?.();

      return notification.success({ message: t('Recruitment request has been accepted successfully') });
    } catch (error) {
      return notification.error({ message: t('Recruitment request has not been accepted successfully ') });
    } finally {
      onComplete?.();
    }
  }, [boarderRecruitmentId, onSuccess, onComplete]);

  return [onAcceptRecruitmentRequest, loading];
};
